import React from 'react';
import { motion } from 'motion/react';
import { books, apps } from '../data';
import { Smartphone } from 'lucide-react';
import BookCover from './BookCover';
import { Link } from 'react-router-dom';

const getBookFallbackImage = (title: string) => {
  const t = title.toLowerCase();
  if (t.includes('pip') || t.includes('pipkin')) {
    return 'https://picsum.photos/seed/flight/450/650';
  }
  if (t.includes('meadow') || t.includes('moonberry') || t.includes('muddle')) {
    return 'https://picsum.photos/seed/meadow/450/650';
  }
  if (t.includes('sleuths') || t.includes('stott')) {
    return 'https://picsum.photos/seed/sleuths/450/650';
  }
  if (t.includes('vow') || t.includes('traitor')) {
    return 'https://picsum.photos/seed/vow/450/650';
  }
  if (t.includes('lies') || t.includes('taste')) {
    return 'https://picsum.photos/seed/lies/450/650';
  }
  if (t.includes('rewind')) {
    return 'https://picsum.photos/seed/rewind/450/650';
  }
  return 'https://picsum.photos/seed/book/450/650';
};

export default function Portfolio() {
  return (
    <div className="bg-white/40 dark:bg-slate-900/10 py-24 sm:py-32 transition-colors duration-300 border-t border-b border-black/5 dark:border-white/5">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        
        {/* Books Section */}
        <div id="books" className="mb-24 sm:mb-32">
          <div className="mx-auto max-w-2xl lg:mx-0 mb-16">
            <h2 className="font-serif text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-50 sm:text-4xl transition-colors duration-300">Our Library</h2>
            <p className="mt-4 text-lg leading-8 text-slate-600 dark:text-slate-355 transition-colors duration-300">
              Discover our latest publications, spanning fiction, non-fiction, and children's literature. Click on any book to read more.
            </p>
          </div>
          
          <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-12 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            {books.map((book, index) => (
              <motion.div 
                key={book.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="w-full"
              >
                <Link
                  to={`/books/${book.id}`}
                  className="flex flex-col h-full rounded-3xl border-2 border-slate-200/80 dark:border-slate-800/80 p-8 bg-white dark:bg-slate-900/80 transition-all duration-300 hover:border-emerald-600/50 dark:hover:border-emerald-500/50 hover:shadow-lg group text-left justify-between"
                  aria-label={`View details of the book: ${book.title}`}
                >
                  <div className="w-full aspect-[3/4] rounded-2xl mb-5 overflow-hidden relative">
                    <BookCover book={book} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="flex-grow flex flex-col justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-1.5 text-[11px] mb-3">
                        {book.tags.map(tag => (
                           <span key={tag} className="relative z-10 rounded-full bg-slate-100 dark:bg-slate-800/80 px-2.5 py-1 font-medium text-slate-600 dark:text-slate-300 transition-colors">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="font-serif text-xl font-semibold leading-snug text-slate-900 dark:text-slate-50 transition-colors group-hover:text-emerald-800 dark:group-hover:text-emerald-400">
                        {book.title}
                      </h3>
                      <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300 transition-colors">
                        {book.description}
                      </p>
                    </div>
                    
                    <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center text-xs font-semibold text-emerald-700 dark:text-emerald-400 transition-colors group-hover:text-emerald-800 dark:group-hover:text-emerald-300">
                      View Book Details <span className="ml-1" aria-hidden="true">→</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        <hr className="mb-24 sm:mb-32 border-slate-100 dark:border-slate-800/80" />

        {/* Apps Section */}
        <div id="apps">
          <div className="mx-auto max-w-2xl lg:mx-0 mb-16">
            <h2 className="font-serif text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-50 sm:text-4xl transition-colors duration-300">Digital Apps</h2>
            <p className="mt-4 text-lg leading-8 text-slate-600 dark:text-slate-300 transition-colors duration-300">
              Innovative tools designed carefully to enhance how we read, write, and experience stories.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
             {apps.map((app: any, index: number) => {
               const CardContent = (
                 <>
                   <div className="aspect-[4/3] w-full bg-slate-200 dark:bg-slate-800 overflow-hidden relative">
                     <img 
                       src={app.image} 
                       alt={app.title} 
                       className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" 
                     />
                   </div>
                   <div className="p-8 flex flex-col flex-grow">
                     <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 tracking-wide uppercase">{app.platform}</div>
                     <h3 className="font-serif text-2xl font-semibold text-slate-900 dark:text-slate-50 mb-4">{app.title}</h3>
                     <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-8 flex-grow">
                       {app.description}
                     </p>
                     <ul className="flex flex-col gap-3">
                       {app.features.map((feature: string, i: number) => (
                         <li key={i} className="flex items-center gap-3 text-sm font-medium text-slate-700 dark:text-slate-300">
                           <div className="h-1.5 w-1.5 rounded-full bg-slate-400 dark:bg-slate-600" />
                           {feature}
                         </li>
                       ))}
                     </ul>
                     {app.link && (
                       <div className="mt-8 pt-6 border-t border-slate-200/60 dark:border-slate-800 inline-flex items-center text-emerald-700 dark:text-emerald-400 text-sm font-semibold transition-colors group-hover:text-emerald-800 dark:group-hover:text-emerald-300">
                         Explore {app.title} <span className="ml-2" aria-hidden="true">→</span>
                       </div>
                     )}
                   </div>
                 </>
               );

               const wrapperClassName = "relative flex flex-col overflow-hidden rounded-3xl bg-slate-50 dark:bg-slate-900/60 ring-1 ring-slate-200/50 dark:ring-slate-800/60 transition-all hover:bg-slate-100/50 dark:hover:bg-slate-900/80 hover:shadow-md group h-full";

               return (
                 <motion.div
                   key={app.id}
                   initial={{ opacity: 0, y: 20 }}
                   whileInView={{ opacity: 1, y: 0 }}
                   viewport={{ once: true }}
                   transition={{ duration: 0.5, delay: index * 0.1 }}
                 >
                   {app.link ? (
                     <Link to={app.link} className={`block ${wrapperClassName}`}>
                       {CardContent}
                     </Link>
                   ) : (
                     <div className={wrapperClassName}>
                       {CardContent}
                     </div>
                   )}
                 </motion.div>
               );
             })}
          </div>
        </div>
        
      </div>
    </div>
  );
}
